<script>
import { api } from "@/state/services";
import { mapState } from "vuex";

export default {
  name: "AiAssistantWidget",
  data() {
    return {
      open: false,
      loading: false,
      conversationId: null,
      messages: [],
      draft: "",
      needsTicket: false,
      ticketForm: {
        subject: "",
        description: "",
        loading: false,
        sentTicketId: null,
      },
    };
  },
  computed: {
    ...mapState({
      account: (state) => state.account,
    }),
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.scrollToBottom();
      }
    },
    avatarUrl() {
      const user = this.account && this.account.user ? this.account.user : null;
      if (user && user.avatar) {
        return user.avatar;
      }
      const name = user && user.name ? user.name : "Você";
      return "https://ui-avatars.com/api/?background=27416E&color=fff&name=" + encodeURIComponent(name);
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.body;
        if (body) {
          body.scrollTop = body.scrollHeight;
        }
      });
    },
    send() {
      const text = this.draft.trim();
      if (!text || this.loading) {
        return;
      }

      this.messages.push({ role: "user", content: text });
      this.draft = "";
      this.loading = true;
      this.needsTicket = false;
      this.scrollToBottom();

      api
        .post("aiassistant/chat", {
          conversation_id: this.conversationId,
          message: text,
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.conversationId = response.data.conversation_id;
            this.messages.push({
              role: "assistant",
              content: response.data.answer,
              sources: response.data.sources || [],
            });

            if (!response.data.confident) {
              this.needsTicket = true;
              this.ticketForm.subject = text.substring(0, 100);
              this.ticketForm.description = this.buildTicketDescription();
            }
          } else {
            this.messages.push({
              role: "assistant",
              content: response.data.message || "Não foi possível responder agora, tente novamente.",
            });
          }
        })
        .catch(() => {
          this.messages.push({
            role: "assistant",
            content: "Não foi possível responder agora, tente novamente em instantes.",
          });
        })
        .finally(() => {
          this.loading = false;
          this.scrollToBottom();
        });
    },
    buildTicketDescription() {
      return this.messages
        .map((m) => (m.role === "user" ? "Consultor: " : "Assistente: ") + m.content)
        .join("\n\n");
    },
    openTicket() {
      if (!this.ticketForm.subject.trim() || !this.ticketForm.description.trim() || this.ticketForm.loading) {
        return;
      }

      this.ticketForm.loading = true;

      api
        .post("aiassistant/open-ticket", {
          conversation_id: this.conversationId,
          subject: this.ticketForm.subject,
          description: this.ticketForm.description,
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.ticketForm.sentTicketId = response.data.ticket.id;
            this.needsTicket = false;
            this.scrollToBottom();
          } else {
            this.messages.push({
              role: "assistant",
              content: response.data.message || "Não foi possível abrir o chamado agora, tente novamente.",
            });
          }
        })
        .catch(() => {
          this.messages.push({
            role: "assistant",
            content: "Não foi possível abrir o chamado agora, tente novamente.",
          });
        })
        .finally(() => {
          this.ticketForm.loading = false;
        });
    },
    goToTicket() {
      const ticketId = this.ticketForm.sentTicketId;
      this.open = false;
      this.$router.push("/tickets/" + ticketId);
    },
  },
};
</script>

<template>
  <div class="ai-assistant-widget">
    <button v-if="!open" type="button" class="ai-assistant-bubble" @click="toggle" aria-label="Abrir assistente virtual">
      <span class="ai-assistant-bubble-ring"></span>
      <img class="ai-assistant-bubble-logo" src="@/assets/images/teams-ia.svg" alt="Teams IA" />
    </button>

    <transition name="ai-assistant-pop">
      <div v-if="open" class="ai-assistant-panel">
        <div class="ai-assistant-header">
          <div class="ai-assistant-header-info">
            <img class="ai-assistant-header-icon" src="@/assets/images/teams-ia.svg" alt="Teams IA" />
            <div class="ai-assistant-header-text">
              <div class="ai-assistant-header-title">Assistente Virtual</div>
              <div class="ai-assistant-header-subtitle">Tire suas dúvidas sobre o sistema</div>
            </div>
          </div>
          <button type="button" class="ai-assistant-close" @click="toggle" aria-label="Fechar assistente virtual">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="ai-assistant-body" ref="body">
          <div v-if="messages.length === 0" class="ai-assistant-welcome">
            <img class="ai-assistant-welcome-icon" src="@/assets/images/teams-ia.svg" alt="Teams IA" />
            <p class="mb-0">Olá! Sou o assistente virtual do app.</p>
            <p class="text-muted font-size-13 mb-0">Pode perguntar como o sistema funciona, comissões, cadastros e mais.</p>
          </div>

          <div v-for="(msg, index) in messages" :key="index" class="ai-assistant-row" :class="msg.role">
            <img v-if="msg.role === 'assistant'" class="ai-assistant-avatar" src="@/assets/images/teams-ia.svg" alt="" />
            <div class="ai-assistant-bubble-text" :class="msg.role">
              {{ msg.content }}
              <div v-if="msg.sources && msg.sources.length" class="ai-assistant-sources">
                <i class="mdi mdi-book-open-page-variant-outline"></i> {{ msg.sources.join(', ') }}
              </div>
            </div>
            <img v-if="msg.role === 'user'" class="ai-assistant-avatar" :src="avatarUrl()" alt="" />
          </div>

          <div v-if="loading" class="ai-assistant-row assistant">
            <img class="ai-assistant-avatar" src="@/assets/images/teams-ia.svg" alt="" />
            <div class="ai-assistant-typing">
              <span></span><span></span><span></span>
            </div>
          </div>

          <div v-if="needsTicket" class="ai-assistant-ticket-card">
            <p class="mb-2 font-size-13">
              <i class="mdi mdi-help-circle-outline"></i>
              Não tenho certeza sobre isso. Quer abrir um chamado com o suporte? Você pode revisar antes de enviar:
            </p>
            <input type="text" class="ai-assistant-input-plain mb-2" v-model="ticketForm.subject" placeholder="Assunto" />
            <textarea class="ai-assistant-input-plain mb-2" rows="3" v-model="ticketForm.description"></textarea>
            <button type="button" class="ai-assistant-cta-btn" :disabled="ticketForm.loading" @click="openTicket">
              <b-spinner v-if="ticketForm.loading" small class="mr-1"></b-spinner>
              Abrir chamado
            </button>
          </div>

          <div v-if="ticketForm.sentTicketId" class="ai-assistant-ticket-card success">
            <p class="mb-1 font-size-13"><i class="mdi mdi-check-circle-outline"></i> Chamado #{{ ticketForm.sentTicketId }} aberto com sucesso.</p>
            <p class="mb-2 font-size-12 text-muted">Você pode acompanhar a qualquer momento em <strong>Suporte &rsaquo; Tickets</strong>.</p>
            <button type="button" class="ai-assistant-cta-btn outline" @click="goToTicket">Ver meu chamado</button>
          </div>
        </div>

        <div class="ai-assistant-footer">
          <input
            type="text"
            class="ai-assistant-input"
            v-model="draft"
            placeholder="Digite sua pergunta..."
            :disabled="loading"
            @keyup.enter="send"
          />
          <button type="button" class="ai-assistant-send" :disabled="loading || !draft.trim()" @click="send" aria-label="Enviar">
            <i class="mdi mdi-send"></i>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.ai-assistant-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1050;
}

/* Bolha flutuante */
/* #122236 = azul-marinho exato da logo teams-ia.svg.
   Sem overflow:hidden no botao: o anel de pulso escala para fora dele. */
.ai-assistant-bubble {
  position: relative;
  display: block;
  box-sizing: border-box;
  width: 64px;
  height: 64px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  line-height: 0;
  -webkit-appearance: none;
  appearance: none;
  background: #122236;
  box-shadow: 0 10px 24px rgba(18, 34, 54, 0.38);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ai-assistant-bubble:hover {
  transform: scale(1.06);
  box-shadow: 0 12px 28px rgba(18, 34, 54, 0.45);
}

.ai-assistant-bubble:focus {
  outline: none;
}

.ai-assistant-bubble:focus-visible {
  box-shadow: 0 0 0 3px #fff, 0 0 0 6px rgba(18, 34, 54, 0.85), 0 10px 24px rgba(18, 34, 54, 0.38);
}

.ai-assistant-bubble-logo {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
}

.ai-assistant-bubble-ring {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  border: 2px solid rgba(18, 34, 54, 0.5);
  pointer-events: none;
  animation: ai-assistant-pulse 2.2s ease-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .ai-assistant-bubble-ring {
    animation: none;
    opacity: 0;
  }

  .ai-assistant-bubble:hover {
    transform: none;
  }
}

@keyframes ai-assistant-pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
}

/* Painel do chat */
.ai-assistant-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 420px;
  max-width: calc(100vw - 32px);
  height: 660px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(15, 25, 45, 0.28);
  overflow: hidden;
}

.ai-assistant-pop-enter-active {
  transition: opacity 0.18s ease, transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.ai-assistant-pop-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.ai-assistant-pop-enter,
.ai-assistant-pop-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.96);
}

/* Cabecalho */
.ai-assistant-header {
  background: linear-gradient(135deg, #1d3357 0%, #27416e 55%, #3f63a5 100%);
  color: #fff;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.ai-assistant-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

/* Anel por box-shadow (nao border) para nao comprimir a imagem de 42px. */
.ai-assistant-header-icon {
  display: block;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #122236;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
  object-fit: cover;
  flex-shrink: 0;
}

.ai-assistant-header-title {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
}

.ai-assistant-header-subtitle {
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ai-assistant-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s ease;
}

.ai-assistant-close:hover {
  background: rgba(255, 255, 255, 0.28);
}

/* Corpo / mensagens */
.ai-assistant-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px;
  background: #f4f6fb;
  display: flex;
  flex-direction: column;
}

.ai-assistant-welcome {
  text-align: center;
  color: #4a5568;
  margin: auto 0;
  padding: 24px 12px;
}

.ai-assistant-welcome-icon {
  display: block;
  width: 84px;
  height: 84px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #122236;
  object-fit: cover;
  box-shadow: 0 8px 20px rgba(18, 34, 54, 0.28);
}

.ai-assistant-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 14px;
  animation: ai-assistant-fade-in 0.2s ease;
}

.ai-assistant-row.user {
  justify-content: flex-end;
}

@keyframes ai-assistant-fade-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-assistant-avatar {
  display: block;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.ai-assistant-bubble-text {
  max-width: 78%;
  word-break: break-word;
  white-space: pre-wrap;
  padding: 10px 14px;
  font-size: 13.5px;
  line-height: 1.45;
  box-shadow: 0 1px 2px rgba(15, 25, 45, 0.06);
}

.ai-assistant-bubble-text.user {
  background: linear-gradient(135deg, #27416e, #3f63a5);
  color: #fff;
  border-radius: 16px 16px 4px 16px;
}

.ai-assistant-bubble-text.assistant {
  background: #fff;
  color: #2d3748;
  border: 1px solid #e7eaf3;
  border-radius: 16px 16px 16px 4px;
}

.ai-assistant-sources {
  margin-top: 6px;
  font-size: 10.5px;
  opacity: 0.65;
}

.ai-assistant-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid #e7eaf3;
  border-radius: 16px 16px 16px 4px;
  padding: 12px 16px;
}

.ai-assistant-typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9aa5b8;
  animation: ai-assistant-bounce 1.1s infinite ease-in-out;
}

.ai-assistant-typing span:nth-child(2) {
  animation-delay: 0.15s;
}
.ai-assistant-typing span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes ai-assistant-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

.ai-assistant-ticket-card {
  background: #fff;
  border: 1px solid #e7eaf3;
  border-radius: 14px;
  padding: 14px;
  margin-top: 4px;
}

.ai-assistant-ticket-card.success {
  background: #eefbf2;
  border-color: #cdf0da;
}

.ai-assistant-input-plain {
  width: 100%;
  border: 1px solid #dde2ee;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  display: block;
  resize: vertical;
}

.ai-assistant-input-plain:focus {
  outline: none;
  border-color: #3f63a5;
}

.ai-assistant-cta-btn {
  border: none;
  background: linear-gradient(135deg, #27416e, #3f63a5);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.ai-assistant-cta-btn.outline {
  background: transparent;
  color: #27416e;
  border: 1px solid #27416e;
}

.ai-assistant-cta-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

/* Rodape / input */
.ai-assistant-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #fff;
  border-top: 1px solid #eef0f5;
  flex-shrink: 0;
}

.ai-assistant-input {
  flex: 1;
  border: 1px solid #e2e6f0;
  background: #f4f6fb;
  border-radius: 999px;
  padding: 11px 18px;
  font-size: 13.5px;
}

.ai-assistant-input:focus {
  outline: none;
  border-color: #3f63a5;
  background: #fff;
}

.ai-assistant-send {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #27416e, #3f63a5);
  color: #fff;
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.12s ease;
}

.ai-assistant-send:disabled {
  opacity: 0.5;
  cursor: default;
}

.ai-assistant-send:not(:disabled):hover {
  transform: scale(1.06);
}

/* Tela cheia no mobile */
@media (max-width: 767.98px) {
  .ai-assistant-panel {
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }

  .ai-assistant-bubble {
    width: 58px;
    height: 58px;
  }

  /* A bolha e `position: fixed` e flutua por cima da lista. O respiro para
     ela nao cobrir paginacao/ultimo card vem do `padding-bottom: 96px` que o
     `.page-content` ganha abaixo de md (design/custom/structure/_topbar.scss);
     aqui so a aproximamos um pouco da borda para sobrar mais tela. */
  .ai-assistant-widget {
    right: 16px;
    bottom: 16px;
  }
}

/* Em navegador de celular a barra de endereco entra e sai da tela, e `100vh`
   fica maior que a area realmente visivel: o rodape do chat (campo de texto e
   botao de enviar) some abaixo do corte. `dvh` acompanha a altura dinamica.
   Fica em regra separada, DEPOIS da acima, para que navegadores sem suporte a
   `dvh` simplesmente a ignorem e continuem com o `100vh`. */
@media (max-width: 767.98px) {
  .ai-assistant-panel {
    height: 100dvh;
    max-height: 100dvh;
  }
}
</style>
